import { type FC, type MouseEventHandler } from "react";
import { Typography, styled, useMediaQuery, useTheme } from "@mui/material";
import type { TypographyProps } from "@mui/material";
import type { DeviceMode } from "../../model/types";
import {
  getCoverTextStyles,
  isHeaderVariant,
  type CoverTextVariant,
  type DesktopTier,
} from "./coverTextStyles";

export interface CoverTextProps {
  text: string;
  variant: CoverTextVariant;
  color: "textPrimary" | "textSecondary";
  weight?: string;
  align: "left" | "center" | "right";
  device: DeviceMode;
  gutterBottom?: boolean;
  onClick?: MouseEventHandler<HTMLElement>;
}

const StyledTypography = styled(Typography, {
  // headingFontFamily тоже фильтруем: без этого emotion пробросит его в DOM
  // и React отругается на неизвестный атрибут.
  shouldForwardProp: (prop) =>
    ![
      "coverAlign",
      "customVariant",
      "weight",
      "device",
      "desktopTier",
      "headingFontFamily",
    ].includes(prop as string),
})<{
  coverAlign: string;
  customVariant: CoverTextVariant;
  weight?: string;
  device: DeviceMode;
  desktopTier: DesktopTier;
  headingFontFamily: string;
}>(
  ({
    theme,
    coverAlign,
    customVariant,
    weight,
    device,
    desktopTier,
    headingFontFamily,
  }) => ({
    textAlign: coverAlign as "left" | "center" | "right",
    whiteSpace: "pre-line",
    // Осознанное отличие от витрины: там текст лежит на полной ширине
    // страницы, здесь - в рамке предпросмотра, и длинное слово без переносов
    // распирало бы её. Обе строки держат текст внутри рамки.
    maxWidth: "100%",
    wordWrap: "break-word",
    // Inner отключает у себя события мыши, чтобы пустое место вокруг текста
    // открывало выбор фона: возвращаем их самому тексту.
    pointerEvents: "auto",
    // Text shadow для лучшей читаемости, как на витрине.
    textShadow:
      theme.palette.mode === "dark"
        ? "0px 0px 30px rgba(0,0,0,0.6)"
        : "0px 0px 30px rgba(255,255,255,0.8)",
    ...getCoverTextStyles({
      variant: customVariant,
      device,
      desktopTier,
      weight,
      headingFontFamily,
    }),
  })
);

export const CoverText: FC<CoverTextProps> = ({
  text,
  variant,
  color,
  weight,
  align,
  device,
  gutterBottom,
  onClick,
}) => {
  const theme = useTheme();
  // В десктопном режиме рамка превью тянется на всю ширину диалога, поэтому
  // её ширина равна ширине окна и настоящий брейкпоинт здесь честный: от lg
  // витрина рисует более крупные размеры. Мобильный режим остаётся на
  // значениях xs независимо от окна (ветвление через byDevice).
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const desktopTier: DesktopTier = isLargeScreen ? "lg" : "md";

  // Импортированные и старые обложки могут прийти без размера: рисуем как
  // subtitle1, а не роняем редактор.
  const safeVariant: CoverTextVariant = variant ?? "subtitle1";
  let muiVariant: TypographyProps["variant"];
  if (isHeaderVariant(safeVariant)) {
    muiVariant = "h1";
  } else if (safeVariant === "subtitle3") {
    muiVariant = "body2";
  } else {
    muiVariant = safeVariant as TypographyProps["variant"];
  }

  return (
    <StyledTypography
      color={color}
      coverAlign={align}
      customVariant={safeVariant}
      variant={muiVariant}
      weight={weight}
      device={device}
      desktopTier={desktopTier}
      headingFontFamily={String(theme.typography.h1.fontFamily)}
      gutterBottom={gutterBottom}
      onClick={onClick}
      sx={onClick ? { cursor: "pointer" } : undefined}
    >
      {text}
    </StyledTypography>
  );
};
